import { useState, useEffect, useCallback } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import {
  UserCheck, UserX, Clock, Plane, Timer, Plus, Loader2, Trash2, IdCard, Briefcase, MapPin,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { EmployeeAvatar } from "./EmployeeAvatar";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SupervisorBadge } from "./SupervisorBadge";
import { AttendanceBioSettings } from "./AttendanceBioSettings";
import { AttendanceGeoSettings } from "./AttendanceGeoSettings";
import { AttendanceShiftSettings } from "./AttendanceShiftSettings";
import { AttendanceRecordExport } from "./AttendanceRecordExport";
import { AttendancePayrollExport } from "./AttendancePayrollExport";
import { useAuth } from "../context/AuthContext";

const monthNow = () => new Date().toISOString().slice(0, 7);
const STATUS = {
  present: { cls: "bg-emerald-100 text-emerald-700", icon: UserCheck },
  late: { cls: "bg-amber-100 text-amber-700", icon: Clock },
  absent: { cls: "bg-rose-100 text-rose-700", icon: UserX },
  leave: { cls: "bg-blue-100 text-blue-700", icon: Plane },
  permission: { cls: "bg-violet-100 text-violet-700", icon: Clock },
};

const KPI = ({ icon: Icon, label, value, tone }) => (
  <div data-testid={`att-kpi-${label}`} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
    <span className={`grid h-10 w-10 place-items-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></span>
    <div><p className="text-xs text-slate-500">{label}</p><p className="font-heading text-xl font-bold text-slate-800 tabular-nums">{value}</p></div>
  </div>
);

const Badge = ({ status }) => {
  const { t } = useLang();
  const s = STATUS[status] || STATUS.present;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}><s.icon className="h-3 w-3" />{t(`dash.att.st.${status}`) }</span>;
};

const EmployeeAttendanceDialog = ({ empId, employee, month, onClose }) => {
  const { t, dir } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    let ok = true;
    api.getEmployeeAttendance(empId, month).then((d) => { if (ok) setData(d); }).catch(() => setData({ records: [], summary: {} }));
    return () => { ok = false; };
  }, [empId, month]);
  const sm = data?.summary || {};
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir={dir} data-testid="att-employee-dialog">
        <DialogHeader><DialogTitle className="text-right">{t("dash.att.recordTitle")} {employee?.name}</DialogTitle></DialogHeader>
        <AttendanceRecordExport employee={employee} month={month} data={data} t={t} />
        {/* Personal / work card */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-blue-50 to-white p-4 flex items-center gap-4">
          <EmployeeAvatar employee={employee} size="lg" />
          <div className="min-w-0 space-y-1 text-sm">
            <p className="font-heading font-bold text-slate-800 text-lg flex items-center gap-2 flex-wrap">{employee?.name} <SupervisorBadge employee={employee} /></p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-500 text-xs">
              {employee?.job_title && <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" />{employee.job_title}</span>}
              {employee?.branch && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{employee.branch}</span>}
              {employee?.employee_number && <span className="inline-flex items-center gap-1"><IdCard className="h-3 w-3" />{employee.employee_number}</span>}
            </div>
          </div>
        </div>
        {/* Monthly summary */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
          {["present", "late", "absent", "leave", "permission"].map((k) => (
            <div key={k} className="rounded-xl border border-slate-200 p-2 text-center">
              <p className="text-xs text-slate-500">{t(`dash.att.st.${k}`)}</p>
              <p className="font-bold tabular-nums text-slate-800">{sm[k] || 0}</p>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 p-2 text-center">
            <p className="text-xs text-slate-500">{t("dash.att.workHours")}</p>
            <p className="font-bold tabular-nums text-slate-800">{sm.hours || 0}</p>
          </div>
        </div>
        {/* Daily records */}
        <div className="mt-3 rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="text-right px-3 py-2 font-medium">{t("dash.att.colDate")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("dash.att.colStatus")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("dash.att.colIn")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("dash.att.colOut")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("dash.att.colHours")}</th>
            </tr></thead>
            <tbody>
              {(data?.records || []).map((r) => (
                <tr key={r.date} className="border-t border-slate-100">
                  <td className="px-3 py-2 tabular-nums">{r.date}</td>
                  <td className="px-3 py-2"><Badge status={r.status} />{r.late_minutes > 0 && <span className="mr-1 text-[11px] font-semibold text-amber-700" data-testid={`att-late-min-${r.date}`}>+{r.late_minutes} د</span>}</td>
                  <td className="px-3 py-2 tabular-nums">{r.check_in || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.check_out || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.hours ?? "—"}</td>
                </tr>
              ))}
              {(!data || data.records.length === 0) && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">{t("dash.att.noMonthRecords")}</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AttendancePanel = ({ canEdit = false, branches = [], employees: allEmployees = [] }) => {
  const employees = allEmployees.filter(isActiveEmp);
  const { company } = useAuth();
  const { t, dir } = useLang();
  const branchNames = branches.map((b) => (typeof b === "string" ? b : b.branch)).filter(Boolean);
  const [month, setMonth] = useState(monthNow());
  const [branch, setBranch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ employee_id: "", date: monthNow() + "-01", status: "present", check_in: "", check_out: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getAttendance(month, branch)); }
    catch { toast.error(t("dash.att.loadFail")); }
    finally { setLoading(false); }
  }, [month, branch, t]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.date) { toast.error(t("dash.att.warnPick")); return; }
    setSaving(true);
    try {
      await api.upsertAttendance({
        employee_id: form.employee_id, date: form.date, status: form.status,
        check_in: form.check_in || null, check_out: form.check_out || null,
      });
      toast.success(t("dash.att.saved"));
      setForm((f) => ({ ...f, check_in: "", check_out: "" }));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("dash.att.saveFail")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const k = data?.kpis || {};
  const empById = Object.fromEntries(employees.map((e) => [e.id, e]));

  return (
    <div className="space-y-5" data-testid="attendance-panel" dir={dir}>
      <div className="flex flex-wrap items-end gap-2">
        <div><label className="text-xs text-slate-500 block">{t("dash.att.month")}</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9" data-testid="att-month" /></div>
        <div><label className="text-xs text-slate-500 block">{t("dash.att.branch")}</label>
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="att-branch-filter">
            <option value="">{t("dash.att.allBranches")}</option>
            {branchNames.map((b) => <option key={b} value={b}>{b}</option>)}
          </select></div>
        <div className="ms-auto"><AttendancePayrollExport month={month} branch={branch} company={company?.name} /></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI icon={UserCheck} label={t("dash.att.kpiPresent")} value={k.present || 0} tone="text-emerald-600 bg-emerald-50" />
        <KPI icon={Clock} label={t("dash.att.kpiLate")} value={k.late || 0} tone="text-amber-600 bg-amber-50" />
        <KPI icon={UserX} label={t("dash.att.kpiAbsent")} value={k.absent || 0} tone="text-rose-600 bg-rose-50" />
        <KPI icon={Plane} label={t("dash.att.kpiLeave")} value={k.leave || 0} tone="text-blue-600 bg-blue-50" />
        <KPI icon={Timer} label={t("dash.att.kpiHours")} value={k.hours || 0} tone="text-violet-600 bg-violet-50" />
      </div>

      {canEdit && (
        <div className="grid lg:grid-cols-2 gap-3 items-start">
          <AttendanceShiftSettings employees={employees} branchNames={branchNames} />
          <AttendanceBioSettings employees={employees} />
          <AttendanceGeoSettings employees={employees} branchNames={branchNames} />
        </div>
      )}

      {canEdit && (
        <form onSubmit={save} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="att-add-form">
          <p className="font-heading font-bold text-slate-700 text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> {t("dash.att.addTitle")}</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="att-employee">
              <option value="">{t("dash.att.pickEmployee")}</option>
              {employees.filter((emp) => emp.is_supervisor || /سعود|saudi|ksa/i.test(emp.nationality || "")).map((emp) => <option key={emp.id} value={emp.id}>{emp.name}{emp.is_supervisor ? " (مشرف داخلي)" : ""}</option>)}
            </select>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="att-date" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="att-status">
              {Object.keys(STATUS).map((k2) => <option key={k2} value={k2}>{t(`dash.att.st.${k2}`)}</option>)}
            </select>
            <div><label className="text-xs text-slate-500">{t("dash.att.checkInTime")}</label><Input type="time" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="att-checkin" /></div>
            <div><label className="text-xs text-slate-500">{t("dash.att.checkOutTime")}</label><Input type="time" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="att-checkout" /></div>
            <div className="flex items-end"><Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 gap-1" data-testid="att-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("dash.common.save")}</Button></div>
          </div>
        </form>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="text-right px-4 py-2.5 font-medium">{t("dash.att.colEmployee")}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t("dash.att.colBranch")}</th>
              <th className="text-center px-3 py-2.5 font-medium">{t("dash.att.kpiPresent")}</th>
              <th className="text-center px-3 py-2.5 font-medium">{t("dash.att.kpiLate")}</th>
              <th className="text-center px-3 py-2.5 font-medium">{t("dash.att.kpiAbsent")}</th>
              <th className="text-center px-3 py-2.5 font-medium">{t("dash.att.kpiLeave")}</th>
              <th className="text-center px-3 py-2.5 font-medium">دقائق التأخير</th>
              <th className="text-center px-3 py-2.5 font-medium">{t("dash.att.colHours")}</th>
            </tr></thead>
            <tbody>
              {(data?.employees || []).map((r) => (
                <tr key={r.employee_id} onClick={() => setDetail(r.employee_id)} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`att-row-${r.employee_id}`}>
                  <td className="px-4 py-2.5 text-blue-700 font-medium"><span className="inline-flex items-center gap-1.5 flex-wrap"><span className="hover:underline">{r.name}</span><SupervisorBadge employee={empById[r.employee_id]} /></span></td>
                  <td className="px-4 py-2.5 text-slate-500">{r.branch || "—"}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-emerald-600 font-semibold">{r.present}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-amber-600">{r.late}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-rose-600">{r.absent}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-blue-600">{r.leave}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-amber-700" data-testid={`att-late-total-${r.employee_id}`}>{r.late_minutes || 0}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums font-semibold">{r.hours}</td>
                </tr>
              ))}
              {(data?.employees || []).length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">{t("dash.att.noRecords")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {detail && <EmployeeAttendanceDialog empId={detail} employee={empById[detail]} month={month} onClose={() => setDetail(null)} />}
    </div>
  );
};
